<script>
import moment from "moment-timezone";
import { getAccountLogo } from "../utils/helpers";
import { SSO_PROVIDERS } from "../utils/constants";
import { parsePhoneNumberFromString, isValidPhoneNumber, AsYouType } from 'libphonenumber-js';
import VuePhoneNumberInput from 'vue-phone-number-input';
import 'vue-phone-number-input/dist/vue-phone-number-input.css';
import AuthShell from "../components/auth/auth-shell.vue";

const { MICROSOFT, GOOGLE } = SSO_PROVIDERS;

export default {
  components: {
    VuePhoneNumberInput,
    AuthShell
  },
  data() {
    return {
      newAccount: {
        organization: "Ashore",
        name: "",
        email: "",
        title: "Your Title",
        phone: "",
        password: "",
        password_confirm: "",
        use_terms_accepted: false,
        data_terms_accepted: false,
      },
      errors: [],
      validated: false,
      password_match: false,
      submited: false,
      working: false,
      signupH1: "",
      signupHeadline: "Create Your Free Account",
      codePlaceHolder: "Your Code",
      isCodeSignup: false,
      affiliate: 1,
      code: "",
      processing: false,
      phoneData: null,
      countryCode: "US", // Default country code
      selectedCountry: "+1",
    };
  },
  methods: {
    updatePhoneNumber(phoneData) {
      this.phoneData = phoneData;

      // Use the entire phone data object from the component
      if (phoneData) {
        this.countryCode = phoneData.countryCode || 'US';
        this.selectedCountry = phoneData.countryCallingCode ? '+' + phoneData.countryCallingCode : '+1';

        // Store full international format for the API
        if (phoneData.formatInternational) {
          this.newAccount.phone = phoneData.formatInternational;
        } else if (phoneData.phoneNumber) {
          this.newAccount.phone = `${this.selectedCountry} ${phoneData.phoneNumber}`;
        }

        if (this.validated) {
          this.checkForm();
        }
      }
    },

    formatPhoneForAPI(phone) {
      if (!phone) return "";

      try {
        // Parse the phone number with libphonenumber-js for proper E.164 formatting
        const parsedNumber = parsePhoneNumberFromString(phone, this.countryCode);
        if (parsedNumber) {
          return parsedNumber.format('E.164'); // Returns format: +15125551000
        }
      } catch (e) {
        console.error("Error formatting phone number:", e);
      }

      // Fallback - just concatenate country code with digits
      const phoneDigits = phone.replace(/\D/g, '');
      return this.selectedCountry + phoneDigits;
    },

    validatePhoneNumber: function(phone) {
      if (!phone || phone.trim() === '') {
        return false;
      }

      if (this.phoneData && this.phoneData.isValid) {
        return this.phoneData.isValid;
      }

      try {
        // Use libphonenumber-js to validate the phone number for the selected country
        return isValidPhoneNumber(phone, this.countryCode);
      } catch (e) {
        console.error("Error validating phone number:", e);
        // Fallback validation - ensure there are enough digits (varies by country)
        const digits = phone.replace(/\D/g, '');
        return digits.length >= 8; // Most countries require at least 8 digits
      }
    },
    ssoClicked: function(ssoMethod){
      window.$A.MarketingService.SSOPreSignupEvent();
      const anchor = document.createElement('a');
      anchor.target = '_blank';
      anchor.innerText = 'SSO';
      const currentDomain = window.location.hostname;
      const attribution = encodeURIComponent(window.$A.MarketingService.GetAttributionJsonString());
      if(ssoMethod === GOOGLE){
        anchor.href = `${window.$A.Env.apiHost}/login/sso/${GOOGLE}?domain=${currentDomain}&attribution=${attribution}`;
      }

      if(ssoMethod === MICROSOFT){
        anchor.href = `${window.$A.Env.apiHost}/login/sso/${MICROSOFT}?domain=${currentDomain}&attribution=${attribution}`;
      }
      document.body.appendChild(anchor);
      anchor.click();
    },
    getParamsAsObject: function() {
      const urlParams = new URLSearchParams(window.location.search);
      const paramsObject = {};
      for (const [key, value] of urlParams.entries()) {
        paramsObject[key] = value;
      }
      return paramsObject;
    },
    resetSubmitted: function () {
      let self = this;
      setTimeout(() => {
        self.submited = false;
      }, 5000)
    },
    async SignUp() {
      if (this.processing) {
        return;
      }
      if (!this.checkForm() || !this.allFormElementsValid() ||
          this.password_match.length === 0 ||
          !this.validatePhoneNumber(this.newAccount.phone)) {
        this.$A.AlertUser(
            this.errors.length
                ? this.errors.join("<br>")
                : "Please fill out the form to create your account.",
            "warning",
            8000
        );
        this.resetSubmitted();
        return;
      }

      this.processing = true;

      try {
        let paramsJsonString = window.$A.MarketingService.GetAttributionJsonString();

        let response = await window.$A.UserService.Register(
            this.newAccount.organization,
            this.newAccount.name,
            this.newAccount.email,
            this.newAccount.password,
            this.formatPhoneForAPI(this.newAccount.phone),
            this.newAccount.title,
            moment.tz.zone(moment.tz.guess()).name,
            this.code,
            paramsJsonString
        );

        if (response && response.id > 0) {
          await window.$A.MarketingService.AshoreAuthSignUpEvent(
              this.newAccount.name,
              this.newAccount.organization,
              this.newAccount.email,
              this.newAccount.title,
              this.newAccount.phone);
        }

      } catch (error) {
        // HandleApiResponse will already show user-friendly error messages
        // through the alert system, so we just need to reset the form state
        this.resetSubmitted();
      } finally {
        this.processing = false;
      }
    },
    validEmail: function (email) {
      let re = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return re.test(email);
    },
    inputValid: function (val) {
      return val ? "has-success" : "has-error";
    },
    allFormElementsValid: function () {
      return (
          this.newAccount.organization &&
          this.newAccount.organization.length !== 0 &&
          this.newAccount.name &&
          this.newAccount.name.length !== 0 &&
          this.newAccount.phone &&
          this.newAccount.phone.length !== 0 &&
          this.newAccount.title &&
          this.newAccount.title.length !== 0 &&
          this.newAccount.password &&
          this.newAccount.password.length !== 0 &&
          this.newAccount.password_confirm &&
          this.newAccount.password_confirm.length !== 0 &&
          this.newAccount.email &&
          this.newAccount.email.length !== 0
      );
    },
    checkForm: function () {
      this.errors = [];
      this.validated = true;

      //Organization
      if (!this.newAccount.organization) {
        this.errors.push("Organization required.");
      }

      //Name
      if (!this.newAccount.name) {
        this.errors.push("Name required.");
      }

      //Title
      if (!this.newAccount.title) {
        this.errors.push("Title required.");
      }

      //Email
      if (!this.newAccount.email) {
        this.errors.push('Email required.');
      } else if (!this.validEmail(this.newAccount.email)) {
        this.errors.push('Please enter a valid email.');
      }

      //Phone
      if (!this.newAccount.phone || this.newAccount.phone.trim() === '') {
        this.errors.push("Phone required.");
      } else if (!this.validatePhoneNumber(this.newAccount.phone)) {
        this.errors.push('Please enter a valid phone number for the selected country.');
      }


      //Password
      if (!this.newAccount.password) {
        this.errors.push('Password required.');
      }

      // Password Confirm
      if (!this.newAccount.password_confirm) {
        this.errors.push('Confirm password required.');
      }

      // Password vs Password Confirm
      if (this.newAccount.password !== this.newAccount.password_confirm) {
        this.errors.push('Password does not match.');
      } else {
        this.password_match = true;
      }

      //Terms of service
      if (!this.newAccount.use_terms_accepted) {
        this.errors.push("Terms of Use required.");
      }

      //Terms of service
      if (!this.newAccount.data_terms_accepted) {
        this.errors.push("Privacy Policy Terms required.");
      }

      //Errors
      if (!this.errors.length) {
        this.submited = true;
        return true;
      }
    },
    validateOnBlur() {
      // Validate phone number when user clicks away
      if (this.newAccount.phone && this.newAccount.phone.trim() !== '') {
        this.validated = true;
        this.checkForm();
      }
    },
  },
  computed: {
    accountLogo: function () {
      return getAccountLogo();
    }
  },
  beforeMount() {
    let query = this.$route.query;

    if (this.$route.path.indexOf("appsumo") !== -1) {
      this.isCodeSignup = 1;
      this.affiliate = 1;
      this.phone = "000.000.0000";
      this.signupH1 = "AppSumo";
      this.codePlaceHolder = "Your AppSumo Code";
      this.signupHeadline = "Create Your New Account";
      this.code = query.c.toLowerCase();
    } else if (query.c !== undefined) {
      this.isCodeSignup = 1;
      this.code = query.c.toLowerCase();
    }
  },
  mounted() {
    // The library's country selector is an <input>, which lands between
    // email and the phone number in the tab order. Skip it when tabbing;
    // it stays clickable (and searchable once opened).
    const countrySelectorInput = this.$el.querySelector(
      ".country-selector .field-input"
    );
    if (countrySelectorInput) {
      countrySelectorInput.setAttribute("tabindex", "-1");
    }
  },
};
</script>

<template>
  <auth-shell :show-panel="true">
    <form role="form" @submit="checkForm()">
      <div v-if="accountLogo" class="auth-logo flex-column align-items-center">
        <img :src="accountLogo" class="account-logo"/>
        <img
            v-show="signupH1 !== ''"
            class="partner-image"
            src="/img/appsumo.png"
            alt="AppSumo"
        />
      </div>
      <h4 class="auth-title">{{ signupHeadline }}</h4>
      <p class="auth-subtitle">Start approving files faster with Ashore.</p>
      <div class="form-group">
        <button type="button" class="auth-btn-ghost" @click="ssoClicked('google')">
          <img src="/img/google-icon.svg" width="18" height="18"/>
          Sign up with Google
        </button>
      </div>
      <div class="form-group">
        <button type="button" class="auth-btn-ghost" @click="ssoClicked('microsoft')">
          <img src="/img/microsoft-icon.svg" width="18" height="18"/>
          Sign up with Microsoft
        </button>
      </div>
      <div class="auth-divider">Or</div>

      <div
          :class="['form-group', inputValid(newAccount.organization)]"
          v-show="false"
      >
        <input
            type="text"
            name="organization"
            autocomplete="new-password"
            class="form-control"
            placeholder="Your Organization"
            v-model="newAccount.organization"
        />
      </div>
      <label class="auth-label">Your Name</label>
      <div cy-data="name"
          :class="['form-group', inputValid(newAccount.name)]">
        <input
            type="text"
            name="name"
            autocomplete="new-password"
            :class="'auth-field' + (validated ? (newAccount.name !== '' ? ' is-valid': ' is-invalid') : '')"
            v-model="newAccount.name"
        />
      </div>
      <label class="auth-label">Your Email</label>
      <div cy-data="email" class="form-group">
        <input
            type="text"
            name="email"
            autocomplete="new-password"
            :class="'auth-field' + (validated ? (validEmail(newAccount.email) ? ' is-valid': ' is-invalid') : '')"
            v-model="newAccount.email"
        />
      </div>
      <label class="auth-label">Your Phone Number</label>
      <div
          cy-data="phone"
          v-show="newAccount.phone !== '000.000.0000'"
          class="form-group phone-input-container"
      >
        <VuePhoneNumberInput
            :value="phoneData ? phoneData.phoneNumber : ''"
            @update="updatePhoneNumber"
            @blur="validateOnBlur"
            :default-country-code="countryCode"
            :no-use-browser-locale="false"
            :color="'#019256'"
            :error="validated && !validatePhoneNumber(newAccount.phone)"
            :error-color="'#dc3545'"
            :valid-color="'#28a745'"
            :required="false"
            :show-code-on-list="true"
            :key="'phone-input'"
            :border-radius="6"
            :text-font-family="'Open Sans'"
            :text-font-size="14"
            :text-font-weight="400"
            :preferred-countries="['US', 'CA', 'GB']"
        />
        <div v-if="validated && !validatePhoneNumber(newAccount.phone)" class="phone-error-message">
          Please enter a valid phone number for the selected country
        </div>
      </div>
      <label class="auth-label">Your Password</label>
      <div
          cy-data="password"
          :class="[
            'form-group',
            inputValid(newAccount.password && newAccount.password_confirm),
          ]"
      >
        <input
            type="password"
            name="password"
            autocomplete="new-password"
            :class="'auth-field' + (validated ? (newAccount.password !== '' ? ' is-valid': ' is-invalid') : '')"
            v-model="newAccount.password"
        />
      </div>
      <label class="auth-label">Confirm Password</label>
      <div
          cy-data="password_confirm"
          :class="[
            'form-group',
            inputValid(newAccount.password_confirm === newAccount.password),
          ]"
      >
        <input
            type="password"
            name="password_confirm"
            autocomplete="new-password"
            :class="'auth-field' + (validated ? (newAccount.password_confirm !== '' && (newAccount.password_confirm === newAccount.password) ? ' is-valid': ' is-invalid') : '')"
            v-model="newAccount.password_confirm"
            v-on:keyup.enter="SignUp()"
        />
      </div>
      <div :class="['form-group']" v-show="isCodeSignup === 1">
        <input
            type="text"
            autocomplete="Code"
            class="auth-field"
            name="code"
            :placeholder="codePlaceHolder"
            v-model="code"
        />
      </div>
      <div class="form-group password-mismatch-note" v-show="!password_match && validated">
        <span>Passwords do not match.</span>
      </div>

      <div
          id="terms-container"
          class="d-flex flex-wrap"
      >
        <b-form-checkbox
            id="use_terms_checkbox"
            v-model="newAccount.use_terms_accepted"
            name="use_terms_checkbox"
        >
          <label class="font-weight-normal terms-style" style="text-align: start"> Yes, I have read and consent
            to the
            <a href="https://ashoreapp.com/terms-of-use/" target="_blank" style="color:#019256">
              Terms of Use
            </a> and
            <a href="https://ashoreapp.com/terms-of-service/" target="_blank" style="color:#019256">
              Terms of Service
            </a>.
          </label>
        </b-form-checkbox>
        <b-form-checkbox
            id="data_terms_checkbox"
            v-model="newAccount.data_terms_accepted"
            class="font-weight-normal"
            name="data_terms_checkbox"
        >
          <label class="font-weight-normal terms-style" style="text-align: start">
            Yes, I have read and consent to the use of my data as described in Ashore's <a
              href="https://ashoreapp.com/privacy/" target="_blank" style="color:#019256">Privacy Policy</a>.
          </label>
        </b-form-checkbox>
      </div>
      <b-button data-cy="signUpButton"
                @click="SignUp()"
                id="signup_button"
                class="auth-btn-primary mt-4"
      >
        <b-spinner
            variant="light"
            v-show="processing"
            type="grow"
            small
        ></b-spinner>
        {{ processing ? 'Creating Account...' : 'Create Your Account' }}
      </b-button>
      <a href="/" class="auth-btn-ghost mt-3">Login Instead</a>
    </form>
  </auth-shell>
</template>

<style scoped>
.account-logo {
  width: 50px;
  height: 50px;
}

/* Custom phone input styling */
.phone-input-container {
  position: relative;
  margin-bottom: 20px; /* Reduce margin, error will appear below */
}

/* Override the library styles so both segments read as one input group
   matching .auth-field (38px, 6px radius, green focus ring) */
::v-deep .vue-phone-number-input {
  display: flex;
}

::v-deep .vue-phone-number-input .select-country-container {
  flex: 0 0 108px;
  width: 108px;
  min-width: 108px;
}

::v-deep .vue-phone-number-input .field-input {
  height: 38px !important;
  min-height: 38px !important;
  border: 1px solid #d1d5db !important;
  box-shadow: 0 1px 2px rgba(8, 11, 13, 0.05) !important;
  background: #fff;
  font-family: 'Open Sans' !important;
  font-size: 14px !important;
  font-weight: 400;
  color: #1f2937 !important;
  padding-top: 0 !important;
  transition: border-color 120ms ease, box-shadow 120ms ease;
}

/* The library floats a label inside the field; we use our own label above */
::v-deep .vue-phone-number-input .field-label {
  display: none;
}

/* Country segment: flat left cap, joined to the number segment */
::v-deep .vue-phone-number-input .country-selector .field-input {
  border-radius: 6px 0 0 6px !important;
  border-right: 0 !important;
  background: #f9fafb;
  padding-left: 38px;
  cursor: pointer;
}

::v-deep .vue-phone-number-input .country-selector .field-country-flag {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  left: 12px;
  margin: 0;
  z-index: 1;
}

/* Number segment: flat right cap */
::v-deep .vue-phone-number-input .input-phone-number .field-input {
  border-radius: 0 6px 6px 0 !important;
}

/* Focus ring matches .auth-field; raise the focused segment so its ring
   isn't clipped by the adjacent segment */
::v-deep .vue-phone-number-input .field-input:focus {
  position: relative;
  z-index: 1;
  border-color: #019256 !important;
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12) !important;
}

/* Error state: red border across the whole group */
::v-deep .vue-phone-number-input.error-phone-number .field-input {
  border-color: #dc3545 !important;
}

/* Country dropdown */
::v-deep .country-list {
  max-height: 200px;
  overflow-y: auto;
  z-index: 99;
  position: absolute;
  background: white;
  border: 1px solid rgba(15, 23, 42, 0.07);
  border-radius: 10px;
  box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14), 0 2px 6px rgba(15, 23, 42, 0.06);
}

/* Custom error message styling - position it below the input */
.phone-error-message {
  display: block;
  width: 100%;
  margin-top: 5px;
  color: #dc3545;
  font-size: 80%;
  text-align: left;
  text-overflow: ellipsis;
}

/* Validation states for the new auth-field inputs */
::v-deep .auth-field.is-valid {
  border-color: #019256;
}

::v-deep .auth-field.is-invalid {
  border-color: #dc3545;
}

::v-deep .auth-field.is-invalid:focus {
  border-color: #dc3545;
  box-shadow: 0 0 0 3px rgba(220, 53, 69, 0.12);
}

.password-mismatch-note span {
  color: #dc3545;
  font-size: 13px;
}

.partner-image {
  width: 100%;
  max-width: 250px;
  margin: 25px auto;
}

.terms-style {
  opacity: 98 !important;
  font-family: 'Open Sans';
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #3E3E40;
}

/* Brand the bootstrap-vue checkboxes green */
::v-deep .custom-control-input:checked ~ .custom-control-label::before {
  background-color: #019256;
  border-color: #019256;
}

::v-deep .custom-control-input:focus ~ .custom-control-label::before {
  box-shadow: 0 0 0 3px rgba(1, 146, 86, 0.12);
}
</style>
